import React from 'react';
import { LatexText, onizlemeKes } from './SoruKarti';
import BenzerlikRozeti from './BenzerlikRozeti';
import { dersRenk, dersLabel, kartBasligi } from '../lib/dersler';
import { puanRenk, puanBaslik } from '../lib/zorlukSeviye';
import { BAGLAM_ROZET_KUCUK } from '../lib/baglamRozet';

// LGS "OPTİK FORM" kartı (2026-10-20, kullanıcı onayı): lgs.* listesinin kartı. Optik işaret (A B C D,
// doğru harf dolu) logo + bileşen olarak durur; kartın altında cevap yerine ders adı (kullanıcı isteği:
// cevap detayda görünür). Ders rengi sol şerit + ad yanında nokta; soru metni Arial (font-serif = Arial).
const HARFLER = ['A', 'B', 'C', 'D'];
const BOYUT = {
  kart: 'h-[18px] w-[18px] text-[10px]',
  logo: 'h-3 w-3',
};

/** Cevap kâğıdı satırı: doğru harf dolu. `etiket=false` harfsiz (logo). */
export function OptikIsaret({ dogru, boyut = 'kart', etiket = true }) {
  return (
    <span className="inline-flex gap-1" role="img" aria-label={dogru ? `Doğru cevap ${dogru}` : 'Cevap girilmemiş'}>
      {HARFLER.map((h) => (
        <span key={h} aria-hidden="true"
          className={`${BOYUT[boyut]} inline-flex items-center justify-center rounded-full border font-medium leading-none ${
            h === dogru ? 'border-lgs-murekkep bg-lgs-murekkep text-white' : 'border-[#C9CED6] text-lgs-soluk'}`}>
          {etiket ? h : null}
        </span>
      ))}
    </span>
  );
}

const DURUM = {
  onaylandi: { ad: 'Onaylandı', renk: '#2F7D5B' },
  onay_bekliyor: { ad: 'Onay bekliyor', renk: '#B7791F' },
  reddedildi: { ad: 'Reddedildi', renk: '#B42318' },
};

export default function LgsKart({ s, secili = false, topluMod = false, yeni = false, onAc }) {
  const ac = () => onAc(s.id);
  const durum = DURUM[s.durum] || { ad: s.durum, renk: '#6B7079' };
  return (
    <article role="button" tabIndex={0} onClick={ac}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ac(); } }}
      aria-pressed={topluMod ? secili : undefined}
      aria-label={`#${s.public_no} ${kartBasligi(s)}`}
      className={`relative flex flex-col rounded-lg border bg-white py-3 pl-4 pr-3 text-lgs-murekkep cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lgs-optik ${
        secili ? 'border-lgs-optik ring-1 ring-lgs-optik' : 'border-lgs-cizgi hover:border-[#BFC5CE]'}`}>
      <span aria-hidden="true" className="absolute bottom-3 left-0 top-3 w-1 rounded-r" style={{ backgroundColor: dersRenk(s.ders) }}
        title={dersLabel(s.ders)} />
      <div className="flex items-center justify-between gap-2 text-[12px]">
        <span className="flex items-center gap-2">
          {topluMod && (
            <span aria-hidden="true" className={`inline-flex h-4 w-4 items-center justify-center rounded border text-[10px] ${
              secili ? 'border-lgs-optik bg-lgs-optik text-white' : 'border-[#BFC5CE]'}`}>{secili ? '✓' : ''}</span>
          )}
          <span className="font-semibold tabular-nums">#{s.public_no}</span>
          {yeni && <span className="text-lgs-optik" title="Son 24 saatte eklendi">yeni</span>}
        </span>
        {s.zorluk_puan != null ? (
          <span className="inline-flex items-center gap-1.5 text-lgs-soluk" title={puanBaslik(s.zorluk_puan)}>
            <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ backgroundColor: puanRenk(s.zorluk_puan) }} />
            Puan {s.zorluk_puan}
          </span>
        ) : <span className="text-lgs-soluk">Puansız</span>}
      </div>

      <h3 className="mt-1.5 line-clamp-2 text-[14px] font-medium leading-snug" title={kartBasligi(s)}>{kartBasligi(s)}</h3>

      {s.onizleme && (
        <div className="mt-2 line-clamp-4 font-serif text-[12.5px] leading-[1.45] text-[#3A3F47]">
          <LatexText>{onizlemeKes(s.onizleme)}</LatexText>
        </div>
      )}

      <div className="mt-auto pt-3">
        {s.kazanim_kodu && (
          <div className="truncate text-[11px] text-lgs-soluk" title={`${s.kazanim_kodu} ${s.kazanim_adi || ''}`}>
            {s.kazanim_kodu}{s.kazanim_adi ? ` ${s.kazanim_adi}` : ''}
          </div>
        )}
        {(s.baglam_grubu || s.benzerlik_komsu) && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {s.baglam_grubu && (
              <span className={BAGLAM_ROZET_KUCUK}>Bağlam {Number(s.grup_sira) || '?'}/{Number(s.grup_toplam) || '?'}</span>
            )}
            <BenzerlikRozeti soru={s} kompakt />
          </div>
        )}
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-[11px]">
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: durum.renk }} />
            {durum.ad}
          </span>
          {/* Ders adı (2026-10-20, kullanıcı isteği: cevap şıkları kartta değil, detayda görünür). */}
          <span className="inline-flex min-w-0 items-center gap-1.5 text-[11px] font-medium">
            <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: dersRenk(s.ders) }} />
            <span className="truncate">{dersLabel(s.ders).replace(/^LGS\s+/, '')}</span>
          </span>
        </div>
      </div>
    </article>
  );
}
